import { useEffect, useState } from "react";

/**
 * Getting a new version onto a phone. An app on the home screen can sit in memory for days, so it checks for a
 * newer build itself: when it comes back to the front and every half hour, it reads the server's page and
 * compares the main script's name (it changes with every build) with the one running now.
 */

const MAIN = /\/assets\/index-[\w-]+\.js/;

/** The main script of the version running now, e.g. "/assets/index-AbC123.js"; null in development. */
function runningBuild(): string | null {
  for (const s of Array.from(document.scripts)) {
    const m = s.src.match(MAIN);
    if (m) return m[0];
  }
  return null;
}

/** True when the server has a newer version than the one on screen. */
export async function newerBuild(): Promise<boolean> {
  const now = runningBuild();
  if (!now) return false;
  try {
    const html = await (await fetch("/", { cache: "no-store" })).text();
    const latest = html.match(MAIN)?.[0];
    return !!latest && latest !== now;
  } catch {
    return false;
  }
}

/** Load the newest version: refresh the offline helper first, then reload the page from the server. */
export async function reloadToLatest() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    await reg?.update();
  } catch {
    // An old or missing offline helper never blocks the reload.
  }
  window.location.reload();
}

/** Whether a newer version is waiting; checked on opening, on coming back to the app and every 30 minutes. */
export function useUpdateAvailable(): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let alive = true;
    const check = () => newerBuild().then((yes) => alive && yes && setAvailable(true));
    const onShow = () => document.visibilityState === "visible" && check();
    check();
    document.addEventListener("visibilitychange", onShow);
    const timer = window.setInterval(check, 30 * 60 * 1000);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onShow);
      window.clearInterval(timer);
    };
  }, []);
  return available;
}
